// @flow
import React, { useState, useEffect } from 'react';
// Components
import Slider from 'react-slick';
import ProductSquare from './ProductSquare';
import { scroller } from 'react-scroll';
import HorizontalDivider from '../../home/components/HorizontalDivider';
import PaginatedScrollerSkeleton from './PaginatedScrollerSkeleton';
// Types
import type { Product } from '../types';
// Styling
import './PaginatedScroller.css';
import 'slick-carousel/slick/slick.css';
//Utils
import { getMinimunScrollerQuantity } from '../utils'

type Props = {
  products: Product[],
  title: string,
  slug: string,
  isLoading: boolean,
  isLoadingPage: boolean,
  hideBottomLine: boolean,
  scrollable: boolean,
  requestNewPage: () => {},
};

// Slick Slider Arrows
const SampleNextArrow = ({ className, style, onClick, isLoadingPage }) => (
  <div
    className={className}
    style={style}
    onClick={(!isLoadingPage && onClick) || null}
  >
    <div className="paginated-scroller-arrow-container">
      {(isLoadingPage && (
        <i
          className="fa fa-spinner fa-pulse fa-2x paginated-scroller-icon"
          aria-hidden="true"
        ></i>
      )) || (
        <i
          className="fa fa-caret-right fa-4x paginated-scroller-icon"
          aria-hidden="true"
        />
      )}
    </div>
  </div>
);

const SamplePrevArrow = ({ className, style, onClick }) => (
  <div className={className} style={style} onClick={onClick}>
    <div className="paginated-scroller-arrow-container">
      <i
        className="fa fa-caret-left fa-4x paginated-scroller-icon"
        aria-hidden="true"
      />
    </div>
  </div>
);

// This is the PaginatedScroller
const PaginatedScroller = (props: Props) => {
  const [minimunScrollerQuantity, setMinimunScrollerQuantity] = useState(getMinimunScrollerQuantity(window.innerWidth))
  
  useEffect(() => {
    const handleResize = () => {
      setMinimunScrollerQuantity(getMinimunScrollerQuantity(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!props.isLoading && props.scrollable && props.products.length > 0) {
      scroller.scrollTo(props.slug, {
        duration: 1500,
        smooth: 'easeInOutQuart',
      });
    }
  }, [props]);

  useEffect(() => {
    setShowMore(true);
  }, [props.products.length]);

  const [showMore, setShowMore] = useState(true);
  const [firstIndexToShow, setFirstIndexToShow] = useState(0);

  useEffect(() => {
    if (!props.isLoading && props.scrollable && props.products.length > 0) {
      scroller.scrollTo(props.slug, {
        duration: 1500,
        smooth: 'easeInOutQuart',
      });
    }
  }, [props]);
  // Note that Product PaginatedScroller must have an even number of items if scroll by 2 will behave properly
  const settings = {
    dots: false,
    infinite: props.products.length > minimunScrollerQuantity,
    autoplay: false,
    accessibility: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    arrows: true,
    swipe: true,
    vertical: false,
    variableWidth: true,
    nextArrow: props.products.length > minimunScrollerQuantity ? <SampleNextArrow isLoadingPage={props.isLoadingPage} /> : <></>,
    prevArrow: props.products.length > minimunScrollerQuantity ? <SamplePrevArrow /> : <></>,
    beforeChange: newIndex =>
      props.requestNewPage ? props.requestNewPage(newIndex) : null,
  };

  if (props.isLoading) {
    return <PaginatedScrollerSkeleton />;
  }

  if (props.products.length > 0) {
    return (
      <div className="paginated-scroller-main">
        <HorizontalDivider
          id={props.slug}
          title={props.title}
          link={props.slug}
        />

        <div className={'paginated-scroller'}>
          <Slider {...settings}>
            {props.products.map((product, i) => (
              <div key={i}>
                <ProductSquare
                  productImage={product.images[0]}
                  productName={product.name}
                  productManufacturer={product.manufacturer}
                  productListPrice={product.listPrice}
                  productPrice={product.price}
                  productUnit={product.unitForQuantity}
                  productSlug={product.slug}
                  productId={product.id}
                  parentProductId={product.productId}
                  productTotalOnHand={product.totalOnHand}
                  defaultQuantity={product.defaultQuantity}
                  incrementQuantity={product.incrementQuantity}
                  inStock={product.inStock}
                  discontinued={product.discontinued}
                  analyticsViewSource={props.title} // USE ANALYTICS
                />
              </div>
            ))}
          </Slider>
        </div>

        <div className="paginated-scroller-mobile">
          <div className="scroller-mobile-products">
            {props.products.map((product, i) => {
              if (i < firstIndexToShow + 4) {
                return (
                  <div key={i}>
                    <ProductSquare
                      productImage={product.images[0]}
                      productName={product.name}
                      productManufacturer={product.manufacturer}
                      productListPrice={product.listPrice}
                      productPrice={product.price}
                      productUnit={product.unitForQuantity}
                      productSlug={product.slug}
                      productId={product.id}
                      parentProductId={product.productId}
                      productTotalOnHand={product.totalOnHand}
                      defaultQuantity={product.defaultQuantity}
                      incrementQuantity={product.incrementQuantity}
                      inStock={product.inStock}
                      discontinued={product.discontinued}
                      analyticsViewSource={props.title} // USE ANALYTICS
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
          {showMore && props.products.length > 4 ? (
            <div className="scroller-mobile-see-more">
              <a
                href="/"
                className="paginated-scroller-mobile-see-more-text"
                onClick={e => {
                  e.preventDefault();
                  setFirstIndexToShow(firstIndexToShow + 4);
                  if (props.requestNewPage) {
                    props.requestNewPage(firstIndexToShow);
                  }
                }}
              >
                Ver mais
              </a>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    );
  } else {
    return <div />;
  }
};

export default PaginatedScroller;
